// @flow
import * as React from 'react';
import { getDateRangeFromPeriod, Periodic, TableSettings, useTableSettings, withTableSettings } from '../../features';
import {darkenHexColor,
  DataGrid,
  DataGridColumn, FilterBadges, letterToColorfulHex, lightenHexColor,
  ListPageWrapper, NextActionFormButton,
  PageTitle, useActionItemsQuery, useNextActionForm,
  useSessionStorageState, withNextActionForm
} from '../../shared';
import { Check, FunnelSimple, X } from '@phosphor-icons/react';
import { Avatar, Box, Button, Divider, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { getRoute } from '../../app/router';
import _ from 'lodash';
import { useNextActionsFilterDrawer, withNextActionsFilterDrawer } from './ui/next-actions-filter-drawer';
import { keycloak } from '@coverright/shared/keycloak';
import { useMemo } from 'react';
import { Tooltip } from '@coverright/ui/inputs';
import {
  ActionItemFilterInput,
  ActionItemOutput,
  ActionItemSortDirection, ActionItemSortField, PageInput, SortDirection
} from '@coverright/data-access/types/enrollment';

const initialFilter: Periodic<ActionItemFilterInput, 'dueDateRange'> = {
  agentId: keycloak?.tokenParsed?.sub
}

export const Page = () => {
  const {open: openEditModal} = useNextActionForm()
  const [page, setPage] = usePage();
  const [sort, setSort] = useSort();
  const [filters, setFilters] = useFilters();
  const {open} = useNextActionsFilterDrawer();
  const {columns} = useTableSettings(tableColumns, filters, settings => setFilters(settings.filters || initialFilter));

  const openFilters = () => open(setFilters, filters);

  const filterInput = useMemo(() => ({
    ...filters,
    dueDateRange: getDateRangeFromPeriod(filters.dueDateRange),
  }), [filters]);

  const {data, loading, refetch} = useActionItemsQuery({
    variables: {
      pageInput: page,
      filter: filterInput,
      sort: sort ? {
        field: sort.field,
        direction: sort.direction.toUpperCase() as ActionItemSortDirection,
      } : undefined,
    }
  })

  const nextActions = data?.actionItems.data || []

  const onFilterRemoveClick = (filterName: keyof ActionItemFilterInput) => {
    setFilters(prev => {
      setPage(p => ({...p, page: 0}));
      return _.omit(prev, filterName)
    })
  };

  return (
    <ListPageWrapper>
      <PageTitle title="Next actions">
        <TableSettings />

        <Button variant={'outlined'}
                onClick={openFilters}
                startIcon={<FunnelSimple weight={'bold'} />}
                size={'small'} sx={{minWidth: 90, alignSelf: 'center'}}>
          Filters
        </Button>

        <Divider orientation={'vertical'} sx={{alignSelf: 'stretch', mx: 2}} />

        <NextActionFormButton variant={'contained'} initialValues={{
          assigneeAgentId: keycloak?.tokenParsed?.sub,
          done: false,
          title: ''
        }} onSubmit={() => refetch()} />
      </PageTitle>

      <FilterBadges onRemoveFilterClick={onFilterRemoveClick} filter={filters} />

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={data?.actionItems.totalElements || 0}
                loading={loading}
                sort={sort}
                onRowClick={row => openEditModal(row, refetch)}
                onSortModelChange={setSort}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={nextActions}/>
    </ListPageWrapper>
  );
};

export const NextActions = withNextActionsFilterDrawer(withTableSettings(withNextActionForm(Page), 'nextActions'));

const tableColumns: DataGridColumn<ActionItemOutput, ActionItemSortField>[] = [
  {
    title: 'Due date',
    value: v => v.dueDate,
    sortKey: ActionItemSortField.DueDate
  },
  {
    title: 'Client',
    value: v => <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Avatar sx={{ bgcolor: lightenHexColor(letterToColorfulHex(v.clientName || ''), 50),
        border: `1px solid ${darkenHexColor(letterToColorfulHex(v.clientName || ''), 10)}`,
        color: darkenHexColor(letterToColorfulHex(v.clientName || ''), 70), width: 20, height: 20, fontSize: 12 }}>
        {_.toUpper(v.clientName?.slice(0, 1))}
      </Avatar>
      <MuiLink component={Link}
               color={'text.primary'}
               onClick={(event: any) => event.stopPropagation()}
               to={`/${getRoute('client')}/${v.clientId}`}>
        {v.clientName}
      </MuiLink>
    </Stack>
  },
  {
    title: 'Agent',
    value: v => v.assigneeAgentName
  },
  {
    title: 'Title',
    value: v => <Box maxWidth={'40vw'} overflow={'hidden'}>
      <Tooltip title={v.title.length > 100 ? v.title : ''}>
        <Typography variant={'body2'} fontWeight={500} width={'fit-content'} maxWidth={'40vw'} overflow={'hidden'}>{v.title}</Typography>
      </Tooltip>
    </Box>
  },
  {
    title: 'Done',
    value: v => v.done ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />
  }
]

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'nextActionsPage',
    { page: 0, size: 20 }
  );
};

const useSort = () => {
  return useSessionStorageState<{field: ActionItemSortField, direction: SortDirection} | undefined>(
    'nextActionsSort',
    {field: ActionItemSortField.DueDate, direction: SortDirection.Asc}
  )
}

const useFilters = () => {
  return useSessionStorageState<Periodic<ActionItemFilterInput, 'dueDateRange'>>(
    'nextActionsFilters',
    initialFilter
  );
};
